import {JsonViewer} from '@textea/json-viewer';
import classNames from 'classnames';
import React, {ComponentProps, ReactElement, useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import Col from '../../common/Col';
import {useEnvironment} from '../../common/EnvironmentProvider';
import Row from '../../common/Row';
import Button from '../../tool/components/buttons/Button';
import {Actions as DebugActions, useGameDebug} from '../debugSlice';
import {GameLogicError} from '../errors';
import {Actions} from '../gameSlice';
import {useCurrentJeopardyWheel, useGameStatus} from '../hooks';
import {EventId} from '../resources/events';
import {LandUseId, useAllLandUses} from '../resources/landUses';
import {PlaceId, useAllPlaces} from '../resources/places';
import {GamePhase, JeopardyWheel, Status} from '../types';

const FastPreparation = ({className, onClick}: ComponentProps<'div'>) => {
  const dispatch = useDispatch();

  const places = useAllPlaces();
  const landUses = useAllLandUses();

  const [placeId, setPlaceId] = useState<PlaceId>();
  const [landUseId, setLandUseId] = useState<LandUseId>();

  const handleContinue = useCallback(() => {
    const place = GameLogicError.assertNonNullish(places.find(p => p.id === placeId));
    const landUse = GameLogicError.assertNonNullish(landUses.find(l => l.id === landUseId));
    dispatch(Actions.setPlace(place));
    dispatch(Actions.prepareGame(landUse));
  }, [dispatch, landUseId, landUses, placeId, places]);

  return (
    <Col
      className={classNames('space-y-4 border bg-gray-50 mb-3 p-xs', className)}
      onClick={e => {
        e.stopPropagation();
        onClick && onClick(e);
      }}
    >
      <div>Fast Preparation</div>
      <select onChange={e => setPlaceId(e.target.value as PlaceId)}>
        <option selected>Select place</option>
        {places.map(place => (
          <option key={`debug-place-${place.id}`} value={place.id}>
            {place.name}
          </option>
        ))}
      </select>
      <select onChange={e => setLandUseId(e.target.value as LandUseId)}>
        <option selected>Select land use</option>
        {landUses.map(landUse => (
          <option key={`debug-landuse-${landUse.id}`} value={landUse.id}>
            {landUse.name}
          </option>
        ))}
      </select>
      {!!placeId && !!landUseId && (
        <button className="border bg-gray-100" onClick={handleContinue}>
          Go to next phase
        </button>
      )}
    </Col>
  );
};

const JeopardyWheelSelector = ({wheel}: {wheel: JeopardyWheel}) => {
  const dispatch = useDispatch();

  const handleSelect = useCallback(
    (eventId: EventId) => {
      dispatch(Actions.executeEvent(eventId));
    },
    [dispatch]
  );

  return (
    <Col
      className={classNames('space-y-4 border bg-gray-50 mb-3 p-xs')}
      onClick={e => {
        e.stopPropagation();
      }}
    >
      <div>{wheel.title}</div>
      <select onChange={e => handleSelect(e.target.value as EventId)}>
        <option selected>Select an event</option>
        {wheel.eventsIds.map(eventId => (
          <option key={eventId} value={eventId}>
            {eventId}
          </option>
        ))}
      </select>
    </Col>
  );
};

const Debug = (): ReactElement | null => {
  const dispatch = useDispatch();
  const environment = useEnvironment();
  const gameStatus = useGameStatus();
  const gameDebug = useGameDebug();
  const currentJeopardyWheel = useCurrentJeopardyWheel();
  const [open, setOpen] = useState(false);

  const toggleOpen = useCallback(() => setOpen(prevState => !prevState), []);

  if (!environment?.debug) return null;
  return (
    <div
      className={classNames(
        'fixed bottom-0 right-0 border bg-gray-50 opacity-90 ml-4 p-1 overflow-scroll cursor-pointer max-w-2xl',
        {'top-0': open}
      )}
    >
      <Button className="text-xs" variant="secondary" onClick={toggleOpen}>
        {!open ? 'OPEN DEBUG' : 'CLOSE'}
      </Button>
      {open && (
        <>
          <div>{`Window: ${formatSize(gameDebug.windowSize)}`}</div>
          <div>{`Board: ${formatSize(gameDebug.boardSize)}`}</div>
          <Row className={'space-x-xs items-center mb-3'}>
            <span>Min board width:</span>
            <input
              className={'min-h-0'}
              type={'number'}
              value={gameDebug.minBoardWidth}
              onChange={evt => dispatch(DebugActions.setMinBoardWidth(parseInt(evt.currentTarget.value)))}
              onClick={evt => evt.stopPropagation()}
            />
          </Row>
          <Row className={'space-x-xs items-center'}>
            <span>Font zoom: {gameDebug.fontZoom}</span>
            <button
              className={'border bg-gray-100 px-4'}
              onClick={evt => {
                evt.stopPropagation();
                console.log('font zoom is', gameDebug.fontZoom);
                dispatch(DebugActions.setFontZoom(gameDebug.fontZoom - 1));
              }}
            >
              -
            </button>
            <button
              className={'border bg-gray-100 px-4'}
              onClick={evt => {
                evt.stopPropagation();
                dispatch(DebugActions.setFontZoom(gameDebug.fontZoom + 1));
              }}
            >
              +
            </button>
          </Row>
          <Row>
            <span>Version: {environment?.version}</span>
          </Row>
          <Row className={'space-x-xs items-center mb-3'}>
            <span>Resize text?</span>
            <input
              className={'min-h-0'}
              type={'checkbox'}
              checked={gameDebug.resizeText}
              onChange={evt => dispatch(DebugActions.setResizeText(evt.currentTarget.checked))}
              onClick={evt => evt.stopPropagation()}
            />
          </Row>
          {gameStatus.phase === GamePhase.preparation && <FastPreparation />}
          {currentJeopardyWheel && <JeopardyWheelSelector wheel={currentJeopardyWheel} />}
          <Row className={'gap-xs'}>
            <button
              className={'border bg-gray-100 px-4'}
              onClick={evt => {
                evt.stopPropagation();
                throw new GameLogicError('Fake async error to test error handling');
              }}
            >
              Async error
            </button>
            <button
              className={'border bg-gray-100 px-4'}
              onClick={evt => {
                evt.stopPropagation();
                dispatch(Actions.setStatus(null as any as Status)); // This will cause an error
              }}
            >
              Render error
            </button>
          </Row>
          <JsonViewer value={gameStatus} enableClipboard={false} />
        </>
      )}
    </div>
  );
};

function formatSize(size?: {width: number; height: number}): string {
  if (!size) return '?';
  return `${size.width.toFixed(0)}x${size.height.toFixed(0)}`;
}

export default Debug;
